import { useState } from "react";
import { finishSession } from "../api.js";
import { readError } from "../errors.js";
import Icon from "../ui/Icon.jsx";

/**
 * T5 를 보낸 직후. finishDebateSession 이 T6 와 결과물을 한 번에 만든다.
 * convictionEnd 는 한 번만 기록되므로 되돌리기를 주지 않는다.
 */
export default function Conviction({ session, onFinished }) {
  const { debate, turns } = session;
  const start = debate.convictionStart ?? 50;
  const [value, setValue] = useState(start);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);

  const t1 = turns.find((t) => t.seq === 1);
  const lastQuestion = [...turns].reverse().find((t) => t.speaker === "ai" && t.meta?.question)?.meta?.question;

  async function finish() {
    if (sending) return;
    setSending(true);
    setErr(null);
    try {
      onFinished(await finishSession(debate.id, value));
    } catch (e) {
      setErr(e);
      setSending(false);
    }
  }

  if (sending) {
    return (
      <main className="screen" style={{ background: "var(--red)", minHeight: "100vh" }}>
        <header className="bar">
          <span className="logo">그래서요<span className="q">?</span></span>
          <span className="grow" />
          <span className="pill">6/6</span>
        </header>
        <section className="card cream raised" aria-live="polite" aria-busy="true"
          style={{ borderRadius: 4, padding: "24px 22px", gap: 16 }}>
          <span className="pill ai" style={{ alignSelf: "flex-start", fontSize: 12 }}>
            {debate.topicSnapshot?.title} · {debate.stanceLabel}
          </span>
          <h1 className="title">여섯 번 다 했어요.<br />카드를 만드는 중이에요</h1>
          <ul style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <li style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, fontWeight: 800 }}>
              <span aria-hidden="true" style={{
                width: 26, height: 26, border: "1px solid var(--line)", borderRadius: 999, background: "var(--ink)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon name="check" size={14} stroke="#FFD84D" width={3.2} />
              </span>
              마지막 반론 받기
            </li>
            <li style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, fontWeight: 800 }}>
              <span aria-hidden="true" style={{ width: 26, height: 26, border: "1px solid var(--line)", borderRadius: 999, background: "var(--red)" }} />
              귀결 카드 뽑는 중
            </li>
            <li style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, fontWeight: 800, color: "var(--faint)" }}>
              <span aria-hidden="true" style={{ width: 26, height: 26, border: "1px solid var(--line)", borderRadius: 999, background: "var(--white)" }} />
              아직 답이 없는 곳 정리
            </li>
          </ul>
        </section>
        <div className="card dashed">
          <p style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.55 }}>여기가 제일 오래 걸려요. AI를 두 번 부르거든요.</p>
          <p style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.55, color: "var(--muted-on-color)" }}>
            여섯 번 주고받은 건 이미 저장돼 있어요. 실패해도 다시 만들면 되고, 오늘 몫이 또 나가지는 않아요.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="screen" style={{ background: "var(--paper)", minHeight: "100vh" }}>
      <header className="bar">
        <span className="logo">그래서요<span className="q">?</span></span>
        <span className="grow" />
        <span className="pill">여섯 번 다 했어요</span>
      </header>

      <h1 className="title">다 하고 나니,<br />이제 확신은 어때요?</h1>

      {t1 && (
        <section className="card cream">
          <span className="label" style={{ color: "var(--muted)" }}>시작할 때 이렇게 말했어요</span>
          <p style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.6 }}>{t1.content}</p>
          <span className="note">그때 확신도 <strong className="num" style={{ fontSize: 18 }}>{start}</strong></span>
        </section>
      )}

      {lastQuestion && (
        /* AI 가 말한 것은 어디서나 card ai(왼쪽 붉은 선)다. alert 는 에러 전용이라
           여기 쓰면 마지막 질문이 경고로 읽힌다. */
        <section className="card ai">
          <span className="label" style={{ color: "var(--red)" }}>AI가 마지막으로 물은 것</span>
          <p style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.6 }}>{lastQuestion}</p>
        </section>
      )}

      <div className="card cream raised">
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          <label htmlFor="conv-end" className="label" style={{ fontSize: 16, paddingBottom: 8 }}>지금 확신도</label>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <span className="num" style={{ fontSize: 18, fontWeight: 800, color: "var(--muted)" }}>{start} →</span>
            <output htmlFor="conv-end" className="d num" style={{ fontSize: 56, lineHeight: 1 }}>{value}</output>
          </div>
        </div>
        <input id="conv-end" className="rng" type="range" min="0" max="100" value={value}
          onChange={(e) => setValue(Number(e.target.value))} />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span className="note">0 · 반반</span>
          <span className="note">100 · 확실</span>
        </div>
      </div>

      <aside className="card dashed" style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
        <Icon name="info" size={20} />
        <p style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.6 }}>
          한 번만 기록돼요. 내려가도 괜찮아요 — 흔들린 것도 결과에 그대로 실려요.
        </p>
      </aside>

      {err && (
        <div role="alert" className="card alert">
          <p style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.5 }}>{readError(err).title}</p>
          <p style={{ fontSize: 13, fontWeight: 700 }}>
            여섯 번 주고받은 건 저장돼 있어요. 다시 눌러도 오늘 몫이 또 나가지 않아요.
          </p>
        </div>
      )}

      <span className="grow" />

      <button type="button" className="btn primary" onClick={finish} style={{ minHeight: 60, fontSize: 18 }}>
        <span>{err ? "다시 시도" : "카드 받으러 가기"}</span>
        <Icon name="arrow" size={20} stroke="currentColor" />
      </button>
    </main>
  );
}
